import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CreateHatavaModal from "./CreateHatavaModal";
import ConfirmationModal from "../MySuppliers/ConfirmationModal";
import { apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import { useSnackbar } from "../../Components/SnackBar";
import { fmtDate, targetText, valueLabel } from "./hatavotUtils";

// "ההטבות שלי" — the supplier's deals under the Coupons & Deals tab, backed by
// GET/POST /hatavot/supplier (COMY1-359). Status is computed by the server;
// every row action re-reads the list so counts/status never drift.
const HatavotList = () => {
  const { t } = useTranslation("hatavot");
  const theme = useTheme();
  const c = theme.palette.customColors;
  const snackbar = useSnackbar();
  const isMobile = useMediaQuery("(max-width:767px)");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, id: null });
  const [busyId, setBusyId] = useState(null);
  // "End deal" is irreversible — it goes through the app's confirm dialog first.
  const [endConfirmId, setEndConfirmId] = useState(null);

  const fetchRows = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setLoadError(false);
      const response = await apiGetRequest(endpoints.hatavot.supplierList);
      if (response.status === 200) setRows(response.data?.hatavot || []);
    } catch (error) {
      console.error("Error fetching hatavot:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const activeRow = rows.find((r) => r.id === menu.id);
  const closeMenu = () => setMenu({ anchor: null, id: null });

  // pause / resume / end — the server answers with the updated row; we still
  // refetch (silently) so redemption counts and computed statuses stay fresh.
  const runAction = async (action, targetId = menu.id) => {
    const id = targetId;
    closeMenu();
    if (!id) return;
    try {
      setBusyId(id);
      const response = await apiPostRequest(endpoints.hatavot[action](id), {});
      const updated = response.data?.hatava;
      if (updated) setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
      await fetchRows({ silent: true });
    } catch (error) {
      console.error(`Error on hatava ${action}:`, error);
      snackbar(
        t(error?.response?.status === 409 ? "list.action_conflict" : "list.action_failed"),
        "error",
      );
    } finally {
      setBusyId(null);
    }
  };

  const STATUS_STYLE = {
    active: { color: c.statusActive, bg: c.brandSoftStrong },
    scheduled: { color: c.info, bg: c.infoSoft },
    paused: { color: c.statusPaused, bg: c.warningSoft },
    pending: { color: c.statusPending, bg: c.warningSoft },
    expired: { color: c.statusExpired, bg: c.statusExpiredSoft },
  };

  const StatusChip = ({ status }) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE.active;
    return (
      <Box sx={{ fontSize: "12px", fontWeight: 600, padding: "3px 10px", borderRadius: "999px", color: s.color, backgroundColor: s.bg, whiteSpace: "nowrap" }}>
        {t(`status.${STATUS_STYLE[status] ? status : "active"}`)}
      </Box>
    );
  };

  const TypeIcon = ({ typeLabel }) => (
    <Box sx={{ width: 40, height: 40, borderRadius: "10px", flex: "none", display: "grid", placeItems: "center", backgroundColor: c.brandSoft, color: c.brand }}>
      {typeLabel === "bundle" ? <Inventory2OutlinedIcon sx={{ fontSize: 20 }} /> : <LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
    </Box>
  );

  const primaryButtonSx = {
    textTransform: "none", fontSize: "15px", fontWeight: 500, borderRadius: "8px", backgroundColor: c.brand, boxShadow: "none",
    "&:hover": { backgroundColor: c.brandHover, boxShadow: "none" },
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "240px" }}>
          <CircularProgress size={48} sx={{ color: c.statusActive }} />
        </Box>
      );
    }
    if (loadError) {
      return (
        <Box sx={{ textAlign: "center", padding: "48px 20px", border: `1px dashed ${c.border}`, borderRadius: "12px" }}>
          <ErrorOutlineIcon sx={{ fontSize: 40, color: c.danger }} />
          <Typography sx={{ fontSize: "15px", color: c.textMedium, marginTop: "10px", marginBottom: "14px" }}>{t("list.load_error")}</Typography>
          <Button onClick={() => fetchRows()} variant="outlined" sx={{ textTransform: "none", borderRadius: "8px", color: c.brand, borderColor: c.brand }}>
            {t("list.retry")}
          </Button>
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <Box sx={{ textAlign: "center", padding: "56px 20px", border: `1px dashed ${c.border}`, borderRadius: "12px" }}>
          <LocalOfferOutlinedIcon sx={{ fontSize: 40, color: c.textDisabled }} />
          <Typography sx={{ fontSize: "15px", fontWeight: 500, color: c.textMedium, marginTop: "10px" }}>{t("list.empty_title")}</Typography>
          <Typography sx={{ fontSize: "13px", color: c.textSoft, marginTop: "6px", maxWidth: "440px", marginInline: "auto" }}>{t("list.empty_hint")}</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {rows.map((row) => (
          <Box key={row.id} sx={{ border: `1px solid ${c.border}`, borderRadius: "12px", backgroundColor: c.surface, padding: "14px 16px", display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: "14px", flexDirection: isMobile ? "column" : "row", minWidth: 0, overflow: "hidden", opacity: busyId === row.id ? 0.6 : 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0, overflow: "hidden", width: isMobile ? "100%" : "auto" }}>
              <TypeIcon typeLabel={row.typeLabel} />
              <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                  <Typography sx={{ fontSize: "15px", fontWeight: 600, color: c.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.title}</Typography>
                  <Box sx={{ fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "6px", backgroundColor: c.chipBg, color: c.textMedium, flex: "none" }}>
                    {t(`type_label.${row.typeLabel === "bundle" ? "bundle" : "discount"}`)}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: "13px", color: c.textSoft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {targetText(row, t)} · {valueLabel(row, t)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", minWidth: 0, width: isMobile ? "100%" : "auto", justifyContent: isMobile ? "space-between" : "flex-end" }}>
              <Box sx={{ textAlign: isMobile ? "start" : "end", minWidth: 0 }}>
                <Typography sx={{ fontSize: "12.5px", color: c.textMedium }}>{fmtDate(row.validFrom)} – {fmtDate(row.validUntil)}</Typography>
                <Typography sx={{ fontSize: "11.5px", color: c.textFaint }}>{t("list.redeemed", { count: row.redeemed ?? 0 })}</Typography>
              </Box>
              <StatusChip status={row.status} />
              <IconButton size="small" disabled={busyId === row.id} onClick={(e) => setMenu({ anchor: e.currentTarget, id: row.id })}>
                <MoreVertIcon sx={{ fontSize: 20, color: c.iconMuted }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const isExpired = activeRow?.status === "expired";

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: c.textStrong }}>
          {t("list.title")} {rows.length > 0 && <Box component="span" sx={{ color: c.textFaint, fontWeight: 500 }}>({rows.length})</Box>}
        </Typography>
        <Button onClick={() => { setEditing(null); setOpenModal(true); }} variant="contained" startIcon={<AddIcon />} sx={primaryButtonSx}>
          {t("list.create")}
        </Button>
      </Box>

      {renderBody()}

      {/* Row actions menu */}
      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }}>
        <MenuItem onClick={() => { setEditing(activeRow); setOpenModal(true); closeMenu(); }} sx={{ fontSize: "14px" }}>{t("list.actions.edit")}</MenuItem>
        {activeRow?.isPaused ? (
          <MenuItem onClick={() => runAction("resume")} sx={{ fontSize: "14px" }} disabled={isExpired}>{t("list.actions.resume")}</MenuItem>
        ) : (
          <MenuItem onClick={() => runAction("pause")} sx={{ fontSize: "14px" }} disabled={isExpired}>{t("list.actions.pause")}</MenuItem>
        )}
        <MenuItem onClick={() => { setEndConfirmId(menu.id); closeMenu(); }} sx={{ fontSize: "14px", color: theme.palette.error.main }} disabled={isExpired}>{t("list.actions.end")}</MenuItem>
      </Menu>

      {endConfirmId && (
        <ConfirmationModal
          open
          actionType="cancel"
          title={t("list.end_confirm.title")}
          text={t("list.end_confirm.text")}
          okText={t("list.end_confirm.confirm")}
          cancelText={t("list.end_confirm.cancel")}
          confirmModalLoading={busyId === endConfirmId}
          handleClose={() => setEndConfirmId(null)}
          handleOpen={async () => {
            const id = endConfirmId;
            await runAction("end", id);
            setEndConfirmId(null);
          }}
        />
      )}

      {openModal && (
        <CreateHatavaModal
          key={editing?.id || "new"}
          open={openModal}
          editHatava={editing}
          onClose={() => { setOpenModal(false); setEditing(null); }}
          onSaved={() => fetchRows({ silent: true })}
        />
      )}
    </Box>
  );
};

export default HatavotList;
